import React, { useEffect, useMemo, useState } from "react";
import type { LottieRefCurrentProps } from "lottie-react";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Lightbulb,
} from "lucide-react";
import { changeBookNums } from "../utils/helpers";
import arrowsImage from "../assets/arrows.png";
import BookmarkAnimation from "../assets/bookmark.json";
import LottieAnimation from "./LottieAnimation";
import MatchWordsAdults from "./MatchWordsAdults";
import HiddenLettersAdults from "./HiddenLettersAdults";
import SentenceExercise from "./SentenceExercise";
import useCtx from "../hooks/useCts";

type Props = {
  book: BookNew;
  selectedPage: number; // indeks (0-based)
  onChangePage: (next: number) => void;
  onOpenSaveBookmarkModal?: () => void;
  primaryColor?: string; // brand: domyślnie #F6851F
};

const BookPDFAdults: React.FC<Props> = ({
  book,
  selectedPage,
  onChangePage,
  onOpenSaveBookmarkModal,
  primaryColor = "#F6851F",
}) => {
  const [showAnswers, setShowAnswers] = useState(false);
  const [activeTab, setActiveTab] = useState<"lesson" | "ex1" | "ex2" | "ex3">(
    "lesson",
  );
  const [bookmarkAnimationLoaded, setBookmarkAnimationLoaded] = useState(false);
  const bookmarkLottieRef = React.useRef<LottieRefCurrentProps | null>(null);
  const shouldAnimateBookmarkRef = React.useRef(true);

  const showStaticBookmark = () => {
    const finalFrame = Math.max(
      (bookmarkLottieRef.current?.getDuration(true) ?? 1) - 1,
      0,
    );

    bookmarkLottieRef.current?.stop();
    bookmarkLottieRef.current?.goToAndStop(finalFrame, true);
  };

  const totalPages = book.pages.length;
  const page = useMemo(
    () => book.pages[selectedPage],
    [book.pages, selectedPage],
  );

  const goPrev = () => onChangePage(Math.max(0, selectedPage - 1));
  const goNext = () => onChangePage(Math.min(totalPages - 1, selectedPage + 1));
  const handleBottomBarPageChange = (direction: "prev" | "next") => {
    setActiveTab("lesson");
    if (direction === "prev") {
      goPrev();
    } else {
      goNext();
    }
    scrollToPageTop();
  };
  const scrollToPageTop = () => {
    window.requestAnimationFrame(() => {
      document
        .getElementById("page")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const n = parseInt(e.target.value, 10);
    if (!Number.isNaN(n)) onChangePage(n);
  };

  const ctx = useCtx();
  const userClient = ctx.clients.find(
    (client) => client.authId === ctx.user.uid,
  );

  const sentenceData = useMemo(() => {
    const segs = page?.segments ?? [];
    return {
      exercises: segs.map((s) => s.exercise ?? s.question),
      answers: segs.map((s) => s.answer),
    };
  }, [page]);

  useEffect(() => {
    setShowAnswers(false);
    setActiveTab("lesson");
    shouldAnimateBookmarkRef.current = true;
  }, [book.id]);

  useEffect(() => {
    shouldAnimateBookmarkRef.current = true;
    bookmarkLottieRef.current?.stop();
    bookmarkLottieRef.current?.goToAndStop(0, true);
    setBookmarkAnimationLoaded(false);
  }, [book.id]);

  useEffect(() => {
    if (activeTab !== "lesson") {
      shouldAnimateBookmarkRef.current = false;
      bookmarkLottieRef.current?.stop();
      return;
    }

    if (!bookmarkAnimationLoaded) return;

    if (!shouldAnimateBookmarkRef.current) {
      showStaticBookmark();
      return;
    }

    bookmarkLottieRef.current?.setSpeed(0.85);

    const timeoutId = window.setTimeout(() => {
      bookmarkLottieRef.current?.setSpeed(0.85);
      bookmarkLottieRef.current?.goToAndPlay(0, true);
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [activeTab, bookmarkAnimationLoaded, book.id]);

  const highlightWords = (text: string, boldWords: string[]) => {
    if (!text) return text;
    if (!boldWords || boldWords.length === 0) return text;

    // lista unikalnych słów do pogrubienia
    const uniqueWords = Array.from(
      new Set(boldWords.map((w) => w.trim().toLowerCase())),
    );

    // tworzymy regex z granicami słów
    const pattern = new RegExp(`\\b(${uniqueWords.join("|")})\\b`, "gi");

    const parts = text.split(pattern);

    return parts.map((part, idx) =>
      uniqueWords.includes(part.toLowerCase()) ? (
        <strong key={idx} className="text-gray-900">
          {part}
        </strong>
      ) : (
        part
      ),
    );
  };

  const hyphenatedTextStyle: React.CSSProperties = {
    hyphens: "auto",
    overflowWrap: "break-word",
    wordBreak: "normal",
  };

  const renderSlashAwareText = (text: string) => {
    const parts = text.split("/");

    return parts.map((part, idx) => (
      <React.Fragment key={`${part}-${idx}`}>
        {part.trim()}
        {idx < parts.length - 1 && (
          <>
            <span aria-hidden="true"> / </span>
            <wbr />
          </>
        )}
      </React.Fragment>
    ));
  };

  const renderWordSeparator = () => (
    <img
      src={arrowsImage}
      alt=""
      aria-hidden="true"
      className="block h-[1em] w-auto max-w-none shrink-0 self-start text-sm mt-1"
    />
  );

  const handleCopyAllQuestions = async () => {
    try {
      const allQuestions = page.segments
        .map((s, idx) => `${idx + 1}. ${s.question}`)
        .join("\n");

      await navigator.clipboard.writeText(allQuestions);
    } catch (err) {
      console.error("Błąd przy kopiowaniu wszystkich pytań", err);
    }
  };

  const handleCopyAllExercises = async () => {
    try {
      const allExercises = page.segments
        .map((s, idx) => `${idx + 1}. ${s.exercise ?? ""}`)
        .filter((line) => line.trim() !== "") // usuwamy puste
        .join("\n");

      await navigator.clipboard.writeText(allExercises);
    } catch (err) {
      console.error("Błąd przy kopiowaniu ćwiczeń", err);
    }
  };

  const handleCopyAllWords = async () => {
    try {
      const allWords = page.words
        .map((w, idx) => `${w.text} — ${w.polish}`)
        .join("\n");

      await navigator.clipboard.writeText(allWords);
    } catch (err) {
      console.error("Błąd przy kopiowaniu słówek", err);
    }
  };

  const showBookmarkLottie = activeTab === "lesson";

  return (
    <div
      id="page"
      className="mt-4 sm:mt-6 max-w-4xl sm:w-full mx-auto pb-8 sm:pb-8 bg-white"
      style={
        {
          minHeight: "400px",
          "--book-accent": primaryColor,
        } as React.CSSProperties
      }
    >
      {/* Exercise tabs */}
      <div className="bg-white">
        <div className="max-w-4xl mx-auto flex">
          {[
            { key: "lesson", label: "Lesson", mobileLabel: "Lesson" },
            { key: "ex1", label: "Exercise 1", mobileLabel: "Ex. 1" },
            { key: "ex2", label: "Exercise 2", mobileLabel: "Ex. 2" },
            { key: "ex3", label: "Exercise 3", mobileLabel: "Ex. 3" },
          ].map((t) => {
            const active = activeTab === (t.key as typeof activeTab);
            return (
              <button
                key={t.key}
                onClick={() => {
                  const y = window.scrollY;
                  setActiveTab(t.key as typeof activeTab);
                  requestAnimationFrame(() => {
                    window.scrollTo({ top: y, behavior: "auto" });
                  });
                }}
                className="relative flex-1 sm:flex-none px-2 sm:px-4 py-3 text-sm sm:text-base font-semibold text-center transition"
                style={{ color: active ? primaryColor : "#475569" }}
              >
                <span className="sm:hidden">{t.mobileLabel}</span>
                <span className="hidden sm:inline">{t.label}</span>
                <span
                  className="absolute left-0 right-0 -bottom-[1px] h-[1px] rounded-full"
                  style={{
                    backgroundColor: active ? primaryColor : "transparent",
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Nawigacja górna ksiązki dla doroslych */}
      <div id="adult-book-navigation" className="sticky top-0 z-30 mb-4">
        <div
          className="relative mx-auto flex items-center justify-between gap-2 overflow-visible p-4 backdrop-blur-xl sm:p-3"
          style={{ backgroundColor: `${primaryColor}1A` }}
        >
          <button
            type="button"
            onClick={onOpenSaveBookmarkModal}
            className={`absolute left-4 top-full z-30 -translate-y-[30%] items-center justify-center opacity-80 ${
              showBookmarkLottie ? "flex" : "hidden"
            }`}
            aria-label="Zapisz zakładkę"
          >
            <LottieAnimation
              key={`adult-bookmark-${book.id}`}
              animationData={BookmarkAnimation}
              autoplay={false}
              loop={false}
              className="h-16 w-16 -scale-x-100 sm:h-24 sm:w-24"
              lottieRef={bookmarkLottieRef}
              onDOMLoaded={() => {
                bookmarkLottieRef.current?.setSpeed(0.85);
                setBookmarkAnimationLoaded(true);
              }}
            />
          </button>

          <div
            className="relative z-20 min-w-0 truncate text-sm font-black sm:pl-2 sm:text-base"
            style={{ color: primaryColor }}
          >
            English Unlocked {book.bookNum}
          </div>

          <div className="relative z-20 flex shrink-0 items-center gap-2 sm:gap-3 ">
            <select
              style={{ backgroundColor: "transparent", color: primaryColor }}
              className="min-w-0 text-right text-sm font-semibold outline-none sm:text-center sm:text-base"
              onChange={handleSelect}
              value={selectedPage}
            >
              {book.pages.map((_, idx) => (
                <option key={idx} value={idx}>
                  Page {changeBookNums(idx + 1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ====== ZAKŁADKA: LESSON ====== */}
      {activeTab === "lesson" && (
        <>
          {/* Wyświetlanie słów */}
          <div className="text-center mt-4">
            <div className="mx-auto mb-4 max-w-2xl">
              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-1.5 text-sm uppercase">
                <span className="min-w-0 text-right font-bold text-gray-800">
                  Vocabulary
                </span>
                {renderWordSeparator()}
                <span className="min-w-0 text-left font-normal text-gray-600">
                  Translation
                </span>
              </div>
              <div className="mx-auto mt-4 h-px w-1/3 bg-black/50" />
            </div>
            <ul className="mx-auto">
              {page.words.map((word, idx) => (
                <li
                  key={idx}
                  className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-1.5 py-0.5"
                >
                  <span
                    lang="en"
                    className="min-w-0 text-right leading-snug"
                    style={hyphenatedTextStyle}
                  >
                    <div className="font-bold tracking-[-0.04em]">
                      {renderSlashAwareText(word.text)}
                    </div>
                  </span>
                  {renderWordSeparator()}
                  <span
                    className="min-w-0 text-left leading-snug tracking-[-0.04em]"
                    style={hyphenatedTextStyle}
                  >
                    <span lang="pl">{renderSlashAwareText(word.polish)}</span>
                    {word.czech && (
                      <>
                        <span aria-hidden="true"> - </span>
                        <span lang="cs">
                          {renderSlashAwareText(word.czech)}
                        </span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mx-auto mt-4 w-11/12 border-t-[4px] border-double border-black" />
          </div>

          {/* Segmenty / pytania */}
          <div className="mt-4 px-3 sm:px-4 text-center">
            {page.segments
              .filter((s) => s && s.question?.trim() !== "")
              .reduce<Segment[][]>((acc, segment, idx) => {
                if (idx % 4 === 0) acc.push([]);
                acc[acc.length - 1].push(segment);
                return acc;
              }, [])
              .map((section, sectionIdx, sections) => {
                const hasDivider = sectionIdx < 5;
                const hasNextSection = sectionIdx < sections.length - 1;

                return (
                  <React.Fragment key={sectionIdx}>
                    <div
                      className={hasDivider ? "" : hasNextSection ? "mb-2" : ""}
                    >
                      <ol className="space-y-2">
                        {section.map((segment, idx) => (
                          <li key={idx}>
                            <div className="flex items-center justify-center gap-2 text-center font-medium tracking-[-0.04em]">
                              <div className="text-gray-700">
                                {sectionIdx * 4 + idx + 1}.{" "}
                                {highlightWords(
                                  segment.question,
                                  segment.boldWords,
                                )}
                              </div>
                            </div>

                            {showAnswers && (
                              <div className="mt-1 text-sm italic text-blue-900">
                                {segment.answer}
                              </div>
                            )}
                          </li>
                        ))}
                      </ol>
                    </div>

                    {hasDivider && (
                      <div
                        id="border"
                        aria-hidden="true"
                        className="pointer-events-none flex items-center justify-center py-8"
                      >
                        {Array.from({ length: 40 }).map((_, i) => (
                          <span
                            key={i}
                            className="h-[6px] w-[6px] shrink-0 rotate-45 border border-black"
                            style={{
                              marginLeft: i === 0 ? 0 : "1px",
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
          </div>

          {/* Must-Have */}
          {page.mustHave && (
            <div className="mt-10 relative w-11/12 mx-auto">
              <div className="bg-slate-100 rounded-lg p-0 pt-6 px-0 flex justify-center">
                <div
                  className="w-11/12"
                  dangerouslySetInnerHTML={{ __html: page.mustHave! }}
                />
              </div>
              <div className="absolute top-0 left-4 -translate-y-1/2 flex items-center space-x-2">
                <div className="bg-gray-500 rounded-full p-1">
                  <Lightbulb size={32} strokeWidth={2.4} color="white" />
                </div>
                <h4 className="px-2 font-bold">Must-Have</h4>
              </div>
            </div>
          )}
        </>
      )}

      {/* ====== ZAKŁADKA: EXERCISE 1 (Matching Words) ====== */}
      {activeTab === "ex1" && (
        <div className="mt-4">
          <MatchWordsAdults
            key={`${book.id}-${selectedPage}`}
            words={page.words}
          />
        </div>
      )}

      {/* ====== ZAKŁADKA: EXERCISE 2 (Hidden Letters) ====== */}
      {activeTab === "ex2" && (
        <div className="mt-4">
          <HiddenLettersAdults words={page.words} primaryColor={primaryColor} />
        </div>
      )}
      {/* ====== EXERCISE 3 (Sentences / luki) ====== */}
      {activeTab === "ex3" && (
        <div className="mt-4 max-w-3xl w-full mx-auto px-2">
          <div className="mb-3 text-center">
            <h4 className="font-bold mb-3"> Ćwiczenie: Uzupełnij zdania</h4>
          </div>

          <SentenceExercise
            exercises={sentenceData.exercises}
            answers={sentenceData.answers}
            showAnswers={showAnswers}
            onCheckedAll={({ correct, total }) => {
              // tu możesz np. logować do historii admina
              // console.log({ action: "exercise_check", correct, total, page: page.num, bookId: book.id });
            }}
          />
        </div>
      )}
      {userClient?.adminEmail === "biuro@anglomania.com.pl" && (
        <div className="mt-8 text-center flex gap-4 justify-center">
          <button
            onClick={handleCopyAllQuestions}
            className="px-4 py-2 rounded-lg shadow font-bold text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Anglomania: Kopiuj pytania
          </button>
          <button
            onClick={handleCopyAllExercises}
            className="px-4 py-2 rounded-lg shadow font-bold text-white bg-emerald-600 hover:bg-emerald-700"
          >
            Anglomania: Kopiuj ćwiczenia
          </button>
          <button
            onClick={handleCopyAllWords}
            className="px-4 py-2 rounded-lg shadow font-bold text-white bg-amber-600 hover:bg-amber-700"
          >
            Anglomania: Kopiuj słówka
          </button>
        </div>
      )}

      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 sm:bottom-6">
        <div
          className="pointer-events-auto flex w-full sm:w-[360px] items-center justify-between rounded-full border border-white/70 p-2 shadow-lg shadow-slate-900/10 backdrop-blur-xl"
          style={{ backgroundColor: `${primaryColor}1A` }}
        >
          <button
            type="button"
            aria-label="Poprzednia strona"
            onClick={() => handleBottomBarPageChange("prev")}
            disabled={selectedPage === 0}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/35 text-slate-700 transition hover:bg-white/55 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
          </button>

          <button
            type="button"
            aria-label={showAnswers ? "Ukryj odpowiedzi" : "Pokaż odpowiedzi"}
            aria-pressed={showAnswers}
            onClick={() => setShowAnswers(!showAnswers)}
            className="flex h-11 min-w-[110px] items-center justify-center gap-2 rounded-full border border-white/50 px-4 transition hover:brightness-95"
            style={{
              backgroundColor: `${primaryColor}A6`,
              color: "#ffffff",
            }}
          >
            {showAnswers ? (
              <Eye size={20} strokeWidth={2.4} />
            ) : (
              <EyeOff size={20} strokeWidth={2.4} />
            )}
            <span className="text-sm font-medium">
              {showAnswers ? "hide answers" : "show answers"}
            </span>
          </button>

          <button
            type="button"
            aria-label="Następna strona"
            onClick={() => handleBottomBarPageChange("next")}
            disabled={selectedPage === totalPages - 1}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/35 text-slate-700 transition hover:bg-white/55 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight size={20} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-12 text-center text-xs sm:tracking-widest text-gray-500">
        © {new Date().getFullYear()} Directpress sp. z o.o.
        <span aria-hidden="true" className="leading-none px-2">
          •
        </span>
        Wszelkie prawa zastrzeżone
      </footer>
    </div>
  );
};

export default BookPDFAdults;
